import React, { useState } from "react";
import { ArrowLeft, Plus, Trash2, X, Save } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/api";
import { useLang } from "@/i18n";
import { COMBO_LIBRARY } from "@/data/workouts";

const authHeader = () => ({ headers: { "X-Access-Token": localStorage.getItem("guardia_bp_token") || "" } });
const ROUND_LENGTHS = [60, 90, 120, 150, 180, 240, 300];
const inputCls = "w-full bg-[#0c0d10] border border-[#272b35] rounded-md px-3 py-2 text-sm text-white focus:border-[#E63946] outline-none";

const DEFAULT = { name: "", warmup_seconds: 180, cooldown_seconds: 120, rest_seconds: 60, rounds: [{ seconds: 180, combos: [] }] };

function NumField({ label, value, onChange, testid, step = 30, max = 900 }) {
  return (
    <label className="block">
      <span className="text-[11px] font-mono uppercase tracking-widest text-zinc-500">{label}</span>
      <input type="number" min={0} max={max} step={step} value={value} data-testid={testid}
        onChange={(e) => onChange(Math.max(0, Math.min(max, Number(e.target.value) || 0)))} className={`${inputCls} mt-1`} />
    </label>
  );
}

function RoundEditor({ round, index, onChange, onRemove, canRemove, lang }) {
  const [custom, setCustom] = useState("");
  const addCombo = (c) => {
    const v = c.trim();
    if (!v || round.combos.length >= 6) return;
    onChange({ ...round, combos: [...round.combos, v.slice(0, 80)] });
  };
  return (
    <div data-testid={`builder-round-${index}`} className="bg-[#0c0d10] border border-[#272b35] rounded-lg p-4">
      <div className="flex items-center justify-between gap-3">
        <span className="font-heading text-lg font-bold uppercase text-white">Round {index + 1}</span>
        <div className="flex items-center gap-2">
          <select value={round.seconds} data-testid={`builder-round-${index}-length`} onChange={(e) => onChange({ ...round, seconds: Number(e.target.value) })}
            className="bg-[#14161b] border border-[#272b35] rounded-md px-2 py-1.5 text-sm text-white font-mono">
            {ROUND_LENGTHS.map((s) => <option key={s} value={s}>{`${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`}</option>)}
          </select>
          {canRemove && (
            <button onClick={onRemove} data-testid={`builder-round-${index}-remove`} className="p-1.5 text-zinc-500 hover:text-[#E63946]"><Trash2 size={16} /></button>
          )}
        </div>
      </div>
      <div className="mt-3 flex flex-wrap gap-2">
        {round.combos.map((c, ci) => (
          <span key={ci} className="inline-flex items-center gap-1 text-xs bg-[#E63946]/15 border border-[#E63946]/40 text-zinc-100 rounded-full pl-3 pr-1.5 py-1">
            {c}
            <button onClick={() => onChange({ ...round, combos: round.combos.filter((_, j) => j !== ci) })} data-testid={`builder-round-${index}-combo-${ci}-remove`}><X size={12} /></button>
          </span>
        ))}
        {!round.combos.length && <span className="text-xs text-zinc-500">{lang === "it" ? "Nessuna combo: shadow libero" : "No combos: free shadow"}</span>}
      </div>
      <div className="mt-3 grid sm:grid-cols-2 gap-2">
        <select value="" data-testid={`builder-round-${index}-combo-select`} onChange={(e) => addCombo(e.target.value)} className={inputCls}>
          <option value="">{lang === "it" ? "+ Aggiungi combo dalla libreria" : "+ Add combo from library"}</option>
          {COMBO_LIBRARY.map((c) => <option key={c} value={c}>{c}</option>)}
        </select>
        <div className="flex gap-2">
          <input value={custom} onChange={(e) => setCustom(e.target.value)} placeholder={lang === "it" ? "Combo personalizzata" : "Custom combo"}
            data-testid={`builder-round-${index}-combo-input`} className={inputCls} />
          <button onClick={() => { addCombo(custom); setCustom(""); }} data-testid={`builder-round-${index}-combo-add`} className="px-3 border border-[#272b35] rounded-md text-white hover:border-[#E63946]"><Plus size={16} /></button>
        </div>
      </div>
    </div>
  );
}

export default function ProgramBuilder({ initial, onSaved, onCancel }) {
  const { lang } = useLang();
  const [p, setP] = useState(initial ? { ...DEFAULT, ...initial } : DEFAULT);
  const [saving, setSaving] = useState(false);
  const set = (k) => (v) => setP((s) => ({ ...s, [k]: v }));
  const setRound = (i, r) => setP((s) => ({ ...s, rounds: s.rounds.map((x, j) => (j === i ? r : x)) }));
  const total = p.warmup_seconds + p.cooldown_seconds + p.rounds.reduce((a, r) => a + r.seconds, 0) + p.rest_seconds * (p.rounds.length - 1);

  const save = async () => {
    if (!p.name.trim()) return toast.error(lang === "it" ? "Dai un nome al programma" : "Name your program");
    setSaving(true);
    const body = { name: p.name.trim(), warmup_seconds: p.warmup_seconds, cooldown_seconds: p.cooldown_seconds, rest_seconds: p.rest_seconds, rounds: p.rounds };
    try {
      const r = initial?.id ? await api.put(`/blueprint/programs/${initial.id}`, body, authHeader()) : await api.post("/blueprint/programs", body, authHeader());
      toast.success(lang === "it" ? "Programma salvato" : "Program saved");
      onSaved(r.data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally { setSaving(false); }
  };

  return (
    <div data-testid="program-builder" className="max-w-2xl mx-auto">
      <button onClick={onCancel} data-testid="builder-back-btn" className="inline-flex items-center gap-1.5 text-sm text-zinc-400 hover:text-white mb-4">
        <ArrowLeft size={15} /> {lang === "it" ? "Programmi" : "Programs"}
      </button>
      <h2 className="font-heading text-3xl font-black uppercase text-white">{initial?.id ? (lang === "it" ? "Modifica programma" : "Edit program") : (lang === "it" ? "Crea il tuo programma" : "Build your program")}</h2>
      <p className="text-sm text-zinc-400 mt-1 font-mono">{lang === "it" ? "Durata totale" : "Total time"}: <span data-testid="builder-total" className="text-white">{Math.round(total / 60)} min</span></p>

      <div className="mt-6 space-y-4">
        <input value={p.name} onChange={(e) => set("name")(e.target.value.slice(0, 60))} data-testid="builder-name-input"
          placeholder={lang === "it" ? "Nome (es. Sparring del martedì)" : "Name (e.g. Tuesday sparring prep)"} className={`${inputCls} text-base py-3`} />
        <div className="grid grid-cols-3 gap-3">
          <NumField label={lang === "it" ? "Riscaldamento (s)" : "Warm-up (s)"} value={p.warmup_seconds} onChange={set("warmup_seconds")} testid="builder-warmup-input" />
          <NumField label={lang === "it" ? "Recupero (s)" : "Rest (s)"} value={p.rest_seconds} onChange={set("rest_seconds")} testid="builder-rest-input" step={15} max={300} />
          <NumField label={lang === "it" ? "Defaticamento (s)" : "Cool-down (s)"} value={p.cooldown_seconds} onChange={set("cooldown_seconds")} testid="builder-cooldown-input" />
        </div>
        {p.rounds.map((r, i) => (
          <RoundEditor key={i} round={r} index={i} lang={lang} canRemove={p.rounds.length > 1}
            onChange={(nr) => setRound(i, nr)} onRemove={() => setP((s) => ({ ...s, rounds: s.rounds.filter((_, j) => j !== i) }))} />
        ))}
        {p.rounds.length < 15 && (
          <button onClick={() => setP((s) => ({ ...s, rounds: [...s.rounds, { seconds: s.rounds[s.rounds.length - 1].seconds, combos: [] }] }))}
            data-testid="builder-add-round-btn" className="w-full py-3 border border-dashed border-[#3a3f4b] rounded-lg text-sm text-zinc-300 hover:border-[#E63946] hover:text-white inline-flex items-center justify-center gap-2">
            <Plus size={16} /> {lang === "it" ? "Aggiungi round" : "Add round"}
          </button>
        )}
      </div>

      <button onClick={save} disabled={saving} data-testid="builder-save-btn"
        className="mt-6 w-full sm:w-auto inline-flex items-center justify-center gap-2 px-8 py-3 bg-[#E63946] hover:bg-[#D90429] text-white font-bold uppercase rounded-md disabled:opacity-50">
        <Save size={16} /> {lang === "it" ? "Salva programma" : "Save program"}
      </button>
    </div>
  );
}
